"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StackingComponentHOC = exports.StackingComponentWrapper = exports.StackingRootContext = exports.FocusLockContext = void 0;
var tslib_1 = require("tslib");
var React = tslib_1.__importStar(require("react"));
var ReactDOM = tslib_1.__importStar(require("react-dom"));
var react_focus_lock_1 = tslib_1.__importDefault(require("react-focus-lock"));
var Declarative_1 = require("./Declarative");
var PortalEventTrap_1 = require("./PortalEventTrap");
var ReactHelpers_1 = require("./ReactHelpers");
var FocusLockContextDefaultValues = {
    enableFocusLock: false,
    returnFocus: false,
};
exports.FocusLockContext = React.createContext(FocusLockContextDefaultValues);
/**
 * The stacking root is the element container into which the mount point for Portals will be appended.
 */
exports.StackingRootContext = React.createContext(null);
exports.StackingRootContext.displayName = 'StackingRootContext';
function getStackingRootElement(stackingRootId, context) {
    var stackingRoot = document.getElementById(stackingRootId) || context || document.body;
    if (!stackingRoot) {
        throw new Error('Could not find stacking root.');
    }
    return stackingRoot;
}
var StackingComponentWrapper = /** @class */ (function (_super) {
    tslib_1.__extends(StackingComponentWrapper, _super);
    function StackingComponentWrapper(props, context) {
        var _this = _super.call(this, props) || this;
        _this.portalChildElement = null;
        _this.handlePortalChildRef = function (portalChildElement) {
            _this.portalChildElement = portalChildElement;
            _this.forceUpdate(); // force the StackingRootContext to re-render; using a State variable here doesn't work because we need it during the first render
        };
        // eslint-disable-next-line deprecation/deprecation
        var stackingRootId = props.stackingRootId;
        if (stackingRootId) {
            console.warn('`stackingRootId` is deprecated and no longer necessary for the vast majority of cases.');
        }
        _this.stackingRoot = getStackingRootElement(stackingRootId, context);
        /*
         * Create the mount point where the stacked element will be mounted and append it to the stacking root.  It should align
         * with the top/left of the stacking root as to align the positioning of stacked elements with the stacking root.  This prevents
         * cases where there are relatively positioned elements above the mount point, thus making the top of the mount point below the top
         * of the stacking root.
         */
        _this.mountPoint = document.createElement('div');
        _this.mountPoint.style.position = 'absolute';
        _this.mountPoint.style.top = '0';
        _this.mountPoint.style.left = '0';
        _this.updateMountPoint();
        return _this;
    }
    StackingComponentWrapper.prototype.componentDidMount = function () {
        this.stackingRoot.appendChild(this.mountPoint);
    };
    StackingComponentWrapper.prototype.componentDidUpdate = function (prevProps) {
        // eslint-disable-next-line deprecation/deprecation
        var _a = this.props, stackingRootId = _a.stackingRootId, wrapperTestId = _a.wrapperTestId, zIndex = _a.zIndex;
        var stackingRoot = getStackingRootElement(stackingRootId, this.context);
        if (stackingRoot !== this.stackingRoot) {
            this.stackingRoot = stackingRoot;
            this.stackingRoot.appendChild(this.mountPoint);
        }
        if (wrapperTestId !== prevProps.wrapperTestId || zIndex !== prevProps.zIndex) {
            this.updateMountPoint();
        }
    };
    StackingComponentWrapper.prototype.componentWillUnmount = function () {
        this.stackingRoot.removeChild(this.mountPoint);
    };
    StackingComponentWrapper.prototype.getPortalChildElement = function () {
        return this.portalChildElement;
    };
    StackingComponentWrapper.prototype.getMountPoint = function () {
        return this.mountPoint;
    };
    StackingComponentWrapper.prototype.getStackingRoot = function () {
        return this.stackingRoot;
    };
    StackingComponentWrapper.prototype.updateMountPoint = function () {
        var _a;
        this.mountPoint.style.zIndex = ((_a = this.props.zIndex) === null || _a === void 0 ? void 0 : _a.toString()) || '';
        this.mountPoint.setAttribute('data-tb-test-id', Declarative_1.makeTestId(this.props.wrapperTestId, 'Root'));
    };
    StackingComponentWrapper.prototype.render = function () {
        var _this = this;
        var portalChild = React.createElement(exports.FocusLockContext.Consumer, null, function (_a) {
            var _b;
            var enableFocusLock = _a.enableFocusLock, returnFocus = _a.returnFocus;
            if (enableFocusLock) {
                // Typically for modal dialogs, we need to trap focus within the modal at all times, even when tabbing back and forth from the address bar.
                // `ReactFocusLock` takes care of this by attaching global event listeners on focusin and focusout, but it only works when
                // all overlays (e.g. popovers and dropdowns) opened from the modal are descendants of the portal child element in the DOM.
                // Such overlays could be non-modal themselves; that's why we are setting `StackingRootContext` but clearing out `FocusLockContext`.
                return React.createElement(exports.StackingRootContext.Provider, { value: (_b = _this.portalChildElement) !== null && _b !== void 0 ? _b : _this.mountPoint },
                    React.createElement(exports.FocusLockContext.Provider, { value: FocusLockContextDefaultValues },
                        React.createElement(react_focus_lock_1.default, { as: PortalEventTrap_1.PortalEventTrap, 
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus: false, children: _this.props.children, lockProps: _this.props.portalEventTrapOverrides, ref: _this.handlePortalChildRef, returnFocus: returnFocus })));
            }
            else {
                return React.createElement(exports.StackingRootContext.Provider, { value: _this.mountPoint },
                    React.createElement(PortalEventTrap_1.PortalEventTrap, tslib_1.__assign({}, _this.props.portalEventTrapOverrides, { children: _this.props.children, ref: _this.handlePortalChildRef })));
            }
        });
        return ReactDOM.createPortal(portalChild, this.mountPoint);
    };
    return StackingComponentWrapper;
}(React.Component));
exports.StackingComponentWrapper = StackingComponentWrapper;
StackingComponentWrapper.contextType = exports.StackingRootContext;
var StackingComponentHOC = function (componentType) {
    var componentClass = ReactHelpers_1.convertToStatefulComponentClass(componentType);
    var newComponentClass = /** @class */ (function (_super) {
        tslib_1.__extends(class_1, _super);
        function class_1() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        class_1.prototype.render = function () {
            return React.createElement(StackingComponentWrapper, tslib_1.__assign({}, this.props, { children: _super.prototype.render.call(this) }));
        };
        return class_1;
    }(componentClass));
    newComponentClass.displayName = "StackingComponentHOC(" + ReactHelpers_1.getComponentName(componentType) + ")";
    return newComponentClass;
};
exports.StackingComponentHOC = StackingComponentHOC;
//# sourceMappingURL=StackingComponent.js.map